<script lang="ts" setup>
import { ref } from "vue";
import H2Heading from "~/components/H2Heading.vue";
import { useHelper } from "~/composables/helper";
import { useUserStore } from "~/store/user";
import type Testimonial from "~~/types/Testimonial";

// Properties

// Data
const userState = ref("ga");

const testimonials = ref([] as Testimonial[]);
const onlyVideo = true;
const limit = 6;

onMounted(async () => {
    userState.value = await useUserStore().fetchUserState();

    testimonials.value = await useHelper().getTestimonials(userState.value, onlyVideo, limit);
    const resultCount = testimonials.value ? Object.keys(testimonials.value).length : 0;
    if (resultCount < limit) {
        const testimonials2 = await useHelper().getTestimonials("", onlyVideo, limit - resultCount, testimonials.value);
        if (testimonials2) {
            testimonials.value = testimonials.value.concat(testimonials2);
        }
    }
});
</script>

<template>
    <div class="bg-abc-gray">
        <!-- Callout -->
        <div class="bg-primary px-4 py-8 text-center text-white">
            <div class="text-lg font-bold">"everything the student needs... self-explanatory... wonderful books."</div>
            <div>&minus; Joyce Harvey, McDonough High School, Louisiana</div>
        </div>

        <div
            v-if="testimonials && Object.keys(testimonials).length"
            class="mt-14 px-4 md:px-8 lg:px-16"
        >
            <H2Heading
                size="text-2xl lg:text-3xl"
                class="mb-4 text-center"
                >Testimonials
            </H2Heading>
            <div
                class="max-h-[800px] gap-8 overflow-y-auto px-4 py-6 text-center sm:grid sm:grid-cols-2 lg:grid-cols-3"
            >
                <div
                    v-for="(data, index) in testimonials"
                    :key="index"
                    class="my-4 inline-block overflow-hidden rounded-2xl border border-gray-300 p-3 shadow-lg sm:my-0 sm:block lg:p-6"
                >
                    <div class="overflow-hidden">
                        <iframe
                            :src="data?.video_url"
                            title="YouTube video player"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media;
                                gyroscope; picture-in-picture; "
                            class="mx-auto"
                            allowfullscreen
                        >
                        </iframe>
                    </div>
                    <div class="flex items-center overflow-hidden pt-4">
                        <div class="hidden w-12 flex-none overflow-hidden rounded-full md:block">
                            <img
                                :src="data?.photo ? data.photo.permalink : '/images/home/Portrait_Placeholder.png'"
                                :alt="`icon - ${data.author}`"
                                class="w-full"
                            />
                        </div>
                        <div class="ml-2 flex-1 text-left leading-tight">
                            <div class="overflow-hidden font-semibold whitespace-nowrap">
                                {{ data?.author }}
                                <span
                                    v-if="data?.position"
                                    class="text-sm font-normal"
                                    >({{ data.position }})</span
                                >
                            </div>
                            <div
                                v-if="data?.organization"
                                class="overflow-hidden whitespace-nowrap"
                            >
                                {{ data.organization }}
                            </div>
                            <div
                                v-if="data?.location"
                                class="text-abc-red overflow-hidden whitespace-nowrap"
                            >
                                {{ data.location }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
